/**
 * Shared banded results table for the unified performance report.
 *
 * This mirrors the structure of the Download view's table
 * (`assets/src/components/usage/AssetHybridReporting.tsx` → `BandedTable`) so the
 * on-screen Report and the Download/CSV surface present the SAME information and
 * layout:
 *
 *   - columns are grouped into logical display groups (Power / Upstream / Weight
 *     / Level / Battery) by their displayKey suffix;
 *   - only columns that actually carry data (non-null in at least one row) are
 *     shown;
 *   - a Start date + End date column lead each row;
 *   - numeric cells render to 2 decimals (null → "—"), right-aligned;
 *   - a preview row limit keeps the on-screen table short.
 *
 * The columns/rows come straight from the render-complete getReport response
 * (`{ displayKey, header }` columns and displayKey-keyed row dicts), so no raw
 * summary column names are exposed.
 */
import { useMemo } from "react"
import { Box, Skeleton, Table, Text } from "@chakra-ui/react"

/** A render-complete column descriptor from the report response. */
export interface ReportResultsColumn {
  displayKey: string
  header: string
}

/** A render-complete wide-table row keyed by displayKey (plus bucket bounds). */
export type ReportResultsRow = Record<string, unknown>

/** Max rows shown in the on-screen preview (mirrors the Download view). */
export const PREVIEW_ROW_LIMIT = 10

// ─── Column grouping (mirrors AssetHybridReporting) ─────────────────────────

interface DisplayGroup {
  key: string
  label: string
  columns: ReportResultsColumn[]
}

/**
 * Suffix → group mapping, matched against a column's displayKey suffix (after
 * the scope/connection prefix). Kept in lockstep with the Download view so both
 * surfaces group columns identically.
 */
const GROUP_PATTERNS: { suffix: string; group: string; label: string }[] = [
  { suffix: "power_average", group: "power", label: "Power" },
  { suffix: "power_peak", group: "power", label: "Power" },
  { suffix: "power_peak_time", group: "power", label: "Power" },
  { suffix: "energy", group: "power", label: "Power" },
  { suffix: "upstream_power_average", group: "upstream", label: "Upstream Power" },
  { suffix: "upstream_energy", group: "upstream", label: "Upstream Power" },
  { suffix: "weight", group: "weight", label: "Weight" },
  { suffix: "level", group: "level", label: "Level" },
  { suffix: "fuel_consumed", group: "level", label: "Level" },
  { suffix: "state_of_charge", group: "battery", label: "Battery" },
  { suffix: "capacity", group: "level", label: "Level" },
]

function classifyColumn(col: ReportResultsColumn): { group: string; label: string } {
  for (const pattern of GROUP_PATTERNS) {
    if (col.displayKey.endsWith(pattern.suffix)) {
      return { group: pattern.group, label: pattern.label }
    }
  }
  return { group: "other", label: "Other" }
}

function buildDisplayGroups(columns: ReportResultsColumn[]): DisplayGroup[] {
  const groupMap = new Map<string, DisplayGroup>()
  for (const col of columns) {
    const { group, label } = classifyColumn(col)
    if (!groupMap.has(group)) {
      groupMap.set(group, { key: group, label, columns: [] })
    }
    groupMap.get(group)!.columns.push(col)
  }
  return Array.from(groupMap.values())
}

// ─── Cell formatting (mirrors the Download view) ────────────────────────────

function formatCell(value: unknown): string {
  if (value == null) return "—"
  if (typeof value === "number") return value.toFixed(2)
  return String(value)
}

export interface ReportResultsTableProps {
  columns: ReportResultsColumn[]
  rows: ReportResultsRow[]
  loading?: boolean
}

/**
 * Render the report's wide results table using the same grouping, active-column
 * filtering, Start/End columns, and 2-decimal formatting as the Download view.
 */
export function ReportResultsTable({ columns, rows, loading = false }: ReportResultsTableProps) {
  // Only surface columns that carry data (non-null in at least one row), exactly
  // like the Download view's activeColumns.
  const activeColumns = useMemo(() => {
    if (rows.length === 0) return columns
    return columns.filter((col) =>
      rows.some((row) => {
        const v = row[col.displayKey]
        return v !== null && v !== undefined
      }),
    )
  }, [columns, rows])

  const groups = useMemo(() => buildDisplayGroups(activeColumns), [activeColumns])
  const allColumns = useMemo(() => groups.flatMap((g) => g.columns), [groups])
  const previewRows = useMemo(() => rows.slice(0, PREVIEW_ROW_LIMIT), [rows])

  return (
    <Table.ScrollArea>
      <Table.Root variant="outline" interactive size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader textAlign="right" fontSize="xs">
              Start date
            </Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right" fontSize="xs">
              End date
            </Table.ColumnHeader>
            {allColumns.map((col) => (
              <Table.ColumnHeader key={col.displayKey} textAlign="right" fontSize="xs">
                {col.header}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {loading ? (
            [...Array(5)].map((_, index) => (
              <Table.Row key={index}>
                <Table.Cell colSpan={allColumns.length + 2}>
                  <Skeleton height="20px" width="100%" />
                </Table.Cell>
              </Table.Row>
            ))
          ) : previewRows.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={allColumns.length + 2} textAlign="center">
                <Box py={4}>
                  <Text color="gray.500">No data available for the selected time range</Text>
                </Box>
              </Table.Cell>
            </Table.Row>
          ) : (
            previewRows.map((row, rowIndex) => (
              <Table.Row key={(row.bucket_start as string) ?? rowIndex}>
                <Table.Cell textAlign="right" fontSize="sm">
                  {String(row.bucket_start ?? "")}
                </Table.Cell>
                <Table.Cell textAlign="right" fontSize="sm">
                  {String(row.bucket_end ?? "")}
                </Table.Cell>
                {allColumns.map((col) => (
                  <Table.Cell key={col.displayKey} textAlign="right" fontSize="sm">
                    {formatCell(row[col.displayKey])}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  )
}

export default ReportResultsTable
